import type { PitcherEnv } from '@sdk/interfaces'
import type {
  SelectedPage as ContentSelectorSelectedPage,
  SelectedFile as ContentSelectorSelectedFile,
  SelectorType,
} from '@content-selector/stores/app'
import type { Selection as ContentSelectorSelection } from '@lib/types/selections'
import type { Canvas, File } from '@openapi'
import type { CanvasSelection } from '@canvas-selector/appTypes'
import type { CLIENT_TYPE } from '@lib/constants/cdp.const'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { CollectionSelectorProps } from '@collection-selector/types/collection-selector.type'
import type { CollectionPlayer } from '@collection-player/types/collection-player.type'
import type { Agenda, AgendaSelectorProps } from '@agenda-selector/types/agenda.type'

export const ADMIN_MESSAGE = 'ADMIN_MESSAGE' as const

export const ADMIN_MESSAGE_TYPES = {
  ENV_CHANGED: 'admin_env_changed',
} as const

export const ADMIN_API_TYPES = {
  ADMIN_REQUEST: 'ADMIN_REQUEST',
  ADMIN_RESPONSE: 'ADMIN_RESPONSE',
} as const

export const ADMIN_API_METHOD_TYPES = {
  CAPTURE_APP_ERROR: 'admin_capture_app_error',
  CREATE_CANVAS: 'admin_create_canvas',
  GET_APP_CONFIG: 'admin_get_app_config',
  GET_CANVAS: 'admin_get_canvas',
  GET_CANVASES: 'admin_get_canvases',
  OPEN_CANVAS_OVERLAY: 'admin_open_canvas_overlay',
  GET_ENV: 'admin_get_env',
  GET_FILE: 'admin_get_file',
  OPEN: 'admin_open_file_overlay',
  CLOSE: 'admin_close_file_overlay',
  GET_FILES: 'admin_get_files',
  GET_INSTANCE_METADATA_TEMPLATES: 'admin_get_instance_metadata_templates',
  GET_USERS: 'admin_get_users',
  TOAST: 'admin_toast',
  UPDATE_CANVAS: 'admin_update_canvas',
  SELECT_COLLECTION_PLAYER_CONTENT: 'admin_select_collection_player_content',
  SELECT_AGENDA_CONTENT: 'admin_select_agenda_content',
  SELECT_CONTENT: 'admin_select_content',
  SELECT_CANVASES: 'admin_select_canvases',
} as const

export type AdminMessagePayloads = {
  [ADMIN_MESSAGE_TYPES.ENV_CHANGED]: AdminEnv
}

/**
 * Error object to send to Pitcher sentry instance
 */
export type AdminCaptureAppErrorRequest = Record<string, any>

/**
 * Represents an admin request with a specific payload.
 * @template Payload - The type of the payload data.
 */
export type AdminRequest<Payload = any> = {
  /**
   * Unique identifier for the request.
   */
  id: string
  /**
   * Type of the admin API request.
   */
  type: (typeof ADMIN_API_TYPES)['ADMIN_REQUEST']
  /**
   * Body of the request containing the method type and payload data.
   */
  body: {
    /**
     * Type of the admin API method.
     */
    type: (typeof ADMIN_API_METHOD_TYPES)[keyof typeof ADMIN_API_METHOD_TYPES]
    /**
     * Payload data for the request.
     */
    data: Payload
  }
}

/**
 * Represents a request to show a toast notification.
 */
export interface AdminToastRequest {
  /**
   * Type of the toast notification.
   */
  type: 'info' | 'error' | 'warning' | 'success' | 'loading'
  /**
   * Message to be displayed in the toast.
   */
  message: string
  /**
   * Duration for which the toast should be displayed (optional).
   */
  duration?: number
  /**
   * Indicates if the toast can be closed manually (optional).
   */
  closable?: boolean
  /**
   * Indicates if the toast should remain visible when hovered over (optional).
   */
  keepAliveOnHover?: boolean
  /**
   * Indicates if an icon should be shown in the toast (optional).
   */
  showIcon?: boolean
}

/**
 * Represents a request to select content.
 */
export type AdminSelectContentRequest = {
  /**
   * List of selections to hydrate the picker with.
   */
  selections?: ContentSelectorSelection[]
  /**
   * Maximum number of selections allowed (optional).
   */
  max_selections?: number
  /**
   * Minimum number of selections allowed (optional).
   */
  min_selections?: number
  /**
   * Type of content to allow selection for (optional).
   * file: Files only
   * slide: Slides only
   * content: Files and slides
   * @default 'content'
   */
  content_type?: 'file' | 'slide' | 'content'
  /**
   * CSS style to apply to the picker (optional).
   */
  style?: Record<string, any>
  /**
   * Array of file ids to include in the picker (optional).
   */
  allowed_ids?: File['id'][]
  /**
   * Array of file extensions to scope out list of the results (optional). Only files with these extensions will be shown.
   */
  allowed_original_extensions?: string[]
  /**
   * Initial metadata filters to apply to the picker (optional).
   */
  initial_metadata_filters?: Record<string, any>
  /**
   * Array of selector types to include in the picker (optional).
   */
  allowed_selector_types?: SelectorType[]
  /**
   * Disables files from selection which are not sharable (optional).
   */
  disable_no_sharable?: boolean
}

/**
 * Represents a response to a content selection request.
 */
export type AdminSelectContentResponse = {
  /**
   * Action taken by the user.
   */
  user_action: 'cancelled' | 'selected'
  /**
   * Selected content, if any.
   */
  content?: (ContentSelectorSelectedFile | ContentSelectorSelectedPage)[]
}

export type AdminSelectCanvasesRequest = {
  /**
   * Array of selections.
   */
  selections?: CanvasSelection[]
  /**
   * Array of canvas types to include in the picker (optional).
   */
  allowed_types?: ('canvas' | 'canvas-template' | 'section' | 'section-template')[]
  /**
   * Array of canvas ids to include in the picker (optional).
   */
  allowed_ids?: Canvas['id'][]
  /**
   * Initial metadata filters to apply to the picker (optional).
   */
  initial_metadata_filters?: Record<string, any>
  /**
   * The sequencing key to use for ordering the canvases (optional).
   */
  sequencing_key?: string
}

export type AdminSelectCanvasesResponse = {
  /** User action result */
  user_action: 'cancelled' | 'selected'
  /** Selected canvases */
  canvases?: Canvas[]
}

/**
 * Represents an admin response with a specific payload.
 * @template Response - The type of the response data.
 */
export type AdminResponse<Response = any> = {
  /**
   * Unique identifier for the response.
   */
  id: string
  /**
   * Type of the admin API response.
   */
  type: (typeof ADMIN_API_TYPES)['ADMIN_RESPONSE']
  /**
   * Body of the response containing the response data.
   */
  body: Response
}

/**
 * Represents an admin message with a specific payload.
 * @template Payload - The type of the message data.
 */
export type AdminMessage<Payload = any> = {
  /**
   * Unique identifier for the message.
   */
  id: string
  /**
   * Type of the admin message.
   */
  type: typeof ADMIN_MESSAGE
  /**
   * Body of the message containing the message type and payload data.
   */
  body: {
    /**
     * Type of the admin message.
     */
    type: (typeof ADMIN_MESSAGE_TYPES)[keyof typeof ADMIN_MESSAGE_TYPES]
    /**
     * Payload data for the message.
     */
    data: Payload
  }
}

/**
 * Represents the admin environment.
 */
export type AdminEnv = {
  /**
   * Pitcher environment settings.
   */
  mode?: PitcherEnv['mode']
  pitcher: PitcherEnv['pitcher']
  launch_darkly?: LaunchDarklyEnv
  client_type?: (typeof CLIENT_TYPE)[keyof typeof CLIENT_TYPE]
  crm_shape?: Record<string, any>
  state?: PitcherEnv['state']
}

export type AdminSelectCollectionPlayerRequest = {
  initial_data?: CollectionSelectorProps['initialData']
  attach_to?: CollectionSelectorProps['attachTo']
  open_settings?: CollectionSelectorProps['openSettings']
  readonly_name?: CollectionSelectorProps['readonlyName']
  close_on_settings?: CollectionSelectorProps['closeOnSettings']
  translations?: CollectionSelectorProps['translations']
  initial_metadata_filters?: CollectionSelectorProps['initialMetadataFilters']
  hide_close?: CollectionSelectorProps['hideClose']
}

export type AdminSelectCollectionPlayerResponse = {
  /** User action result */
  user_action: 'cancelled' | 'selected' | 'settings'
  /** Selected content */
  content?: CollectionPlayer
}

export type AdminSelectAgendaRequest = {
  initial_agenda?: AgendaSelectorProps['initialAgenda']
  attach_to?: AgendaSelectorProps['attachTo']
}

export type AdminSelectAgendaResponse = {
  /** User action result */
  user_action: 'cancelled' | 'selected'
  /** Selected content */
  content?: Agenda
}
